import { cn } from "@/shared/lib/utils"
import type { CSSProperties, ElementType, ReactNode } from "react"

type Props = {
  /**
   * Content rendered inside the flex container.
   */
  children: ReactNode

  /**
   * Extra classes appended to the generated Tailwind classes.
   * Use this for Tailwind tokens like `gap-4`, `p-6`, `w-full`, etc.
   */
  className?: string

  /**
   * HTML tag to render.
   * Useful when you need `section`, `nav`, `ul`, etc.
   */
  as?: ElementType

  /**
   * Optional id forwarded to the rendered element.
   */
  id?: string

  /**
   * Optional role forwarded to the rendered element.
   */
  role?: string

  /**
   * Optional aria-label forwarded to the rendered element.
   */
  ariaLabel?: string

  /**
   * Direction of the flex container.
   * - row: left to right
   * - col: top to bottom
   * - rowRev: right to left
   * - colRev: bottom to top
   */
  dir?: "row" | "col" | "rowRev" | "colRev"

  /**
   * Justification along the main axis.
   * - start: pack at the start
   * - end: pack at the end
   * - center: center items
   * - between: space between items
   * - around: space around items
   * - evenly: even spacing
   */
  just?: "start" | "end" | "center" | "between" | "around" | "evenly"

  /**
   * Alignment along the cross axis.
   * - stretch: stretch items (default)
   * - start: align to start
   * - end: align to end
   * - center: align to center
   * - base: baseline alignment
   */
  align?: "stretch" | "start" | "end" | "center" | "base"

  /**
   * Wrapping behavior.
   * - no: do not wrap
   * - wrap: wrap to next line
   * - wrapRev: wrap in reverse
   */
  wrap?: "no" | "wrap" | "wrapRev"

  /**
   * Gap between children.
   * Number is interpreted as px.
   * String supports any CSS length, for example `12px`, `1rem`, `2vh`.
   * If you want Tailwind `gap-*`, pass it via `className`.
   */
  gap?: number | string

  /**
   * flex-grow value for the container.
   * Use 1 to let it grow to fill available space.
   */
  grow?: 0 | 1

  /**
   * flex-shrink value for the container.
   * Use 0 to prevent shrinking.
   */
  shrink?: 0 | 1

  /**
   * flex-basis value for the container.
   * Useful for fixed or percentage sizing.
   */
  basis?: CSSProperties["flexBasis"]

  /**
   * Optional min-width forwarded via inline style.
   */
  minW?: CSSProperties["minWidth"]

  /**
   * Optional max-width forwarded via inline style.
   */
  maxW?: CSSProperties["maxWidth"]

  /**
   * Optional min-height forwarded via inline style.
   */
  minH?: CSSProperties["minHeight"]

  /**
   * Optional max-height forwarded via inline style.
   */
  maxH?: CSSProperties["maxHeight"]

  /**
   * Inline style merged last.
   * Prefer Tailwind via `className` unless you need dynamic values.
   */
  style?: CSSProperties
}

/**
 * Flex
 *
 * Flexbox layout primitive with readable, explicit props.
 * This component is server compatible by default.
 * You only need `"use client"` if you attach event handlers or use client hooks in the same file.
 *
 * Examples:
 * ```tsx
 * <Flex>...</Flex>
 *
 * <Flex dir="col" gap={16}>...</Flex>
 *
 * <Flex just="between" align="center" className="w-full">...</Flex>
 *
 * <Flex dir="rowRev" just="around" gap="2vh">...</Flex>
 *
 * <Flex dir="col" just="center" align="center" className="min-h-screen">...</Flex>
 *
 * <Flex wrap="wrap" className="gap-3">...</Flex>
 * ```
 */
export function Flex(props: Props) {
  const cls = cn(
    "flex",
    dirCls(props.dir),
    wrapCls(props.wrap),
    justCls(props.just),
    alignCls(props.align),
    props.className
  )

  const st = mergeStyle(props.style, {
    gap: gapVal(props.gap),
    flexGrow: props.grow,
    flexShrink: props.shrink,
    flexBasis: props.basis,
    minWidth: props.minW,
    maxWidth: props.maxW,
    minHeight: props.minH,
    maxHeight: props.maxH,
  })

  const Tag = (props.as ?? "div") as ElementType

  return (
    <Tag
      id={props.id}
      role={props.role}
      aria-label={props.ariaLabel}
      className={cls}
      style={st}
    >
      {props.children}
    </Tag>
  )
}

/**
 * Maps direction prop to Tailwind class.
 *
 * @param dir Direction value.
 * @returns Tailwind class for direction.
 */
function dirCls(dir?: Props["dir"]): string {
  if (!dir || dir === "row") return "flex-row"
  if (dir === "col") return "flex-col"
  if (dir === "rowRev") return "flex-row-reverse"
  return "flex-col-reverse"
}

/**
 * Maps wrap prop to Tailwind class.
 *
 * @param wrap Wrap value.
 * @returns Tailwind class for wrapping.
 */
function wrapCls(wrap?: Props["wrap"]): string {
  if (!wrap || wrap === "no") return "flex-nowrap"
  if (wrap === "wrap") return "flex-wrap"
  return "flex-wrap-reverse"
}

/**
 * Maps just prop to Tailwind class.
 *
 * @param just Justification value.
 * @returns Tailwind class for justification.
 */
function justCls(just?: Props["just"]): string {
  if (!just || just === "start") return "justify-start"
  if (just === "end") return "justify-end"
  if (just === "center") return "justify-center"
  if (just === "between") return "justify-between"
  if (just === "around") return "justify-around"
  return "justify-evenly"
}

/**
 * Maps align prop to Tailwind class.
 *
 * @param align Alignment value.
 * @returns Tailwind class for alignment.
 */
function alignCls(align?: Props["align"]): string {
  if (!align || align === "stretch") return "items-stretch"
  if (align === "start") return "items-start"
  if (align === "end") return "items-end"
  if (align === "center") return "items-center"
  return "items-baseline"
}

/**
 * Normalizes gap to a CSS value.
 *
 * @param gap Gap value, number is px, string is a CSS length.
 * @returns A CSS gap value or undefined.
 */
function gapVal(gap?: Props["gap"]): CSSProperties["gap"] | undefined {
  if (gap === undefined) return undefined
  if (typeof gap === "number") return `${gap}px`
  const v = gap.trim()
  return v ? v : undefined
}

/**
 * Merges styles while skipping undefined values from `next`.
 *
 * @param base Existing style.
 * @param next Partial style to apply.
 * @returns Merged style, or base if next is empty.
 */
function mergeStyle(
  base: CSSProperties | undefined,
  next: CSSProperties
): CSSProperties | undefined {
  let has = false
  for (const k in next) {
    const key = k as keyof CSSProperties
    if (next[key] !== undefined) {
      has = true
      break
    }
  }
  if (!has) return base
  if (!base) return next
  return { ...base, ...next }
}
